import { Box, Button, Dialog, DialogActions, DialogContent, Stack, Typography } from '@mui/material';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import Drawing from '/@/components/drawing';
import Icon from '/@/components/icon';
import MyRecommendationsMobileCard from '/@/components/my-recommendations/mobile-card';
import MyRecommendationsTable from '/@/components/my-recommendations/table';
import Card from '/@/components/new-home/card';
import settings from '/@/core/settings';
import { useCropMonitorings } from '/@/hooks/crop-monitoring/use-crop-monitorings';
import useMyRecommendations from '/@/hooks/my-recommendations/useFilterMyRecommendations';
import { _ } from '/@/locale';
import {
    setDensity,
    setEnvironment,
    setHumidity,
    setPlantingDate,
    setRecommendation,
} from '/@/store/crop-monitoring/actions';
import { setSelectedFeature } from '/@/store/nav/actions';
import useWindowSize from '/@/utils/hooks/window-size';
import { reverse } from '/@/utils/router';
import Input from '/@/v3/components/forms/input';
import { seedRecommendationUrl } from '/@/views/recommendations/seed-recommendation-route';
import Alert from '../../bayer/alert';
import styles from './styles.module.css';

const DataSourceStepper = ({ selected, handleNext, handleBack }) => {
    const history = useNavigate();
    const dispatch = useDispatch();
    const windowWidth = useWindowSize().width;
    const isMobile = windowWidth <= 900;
    const isMobileXl = windowWidth >= 612;
    const setSelectedReco = bindActionCreators(setRecommendation, dispatch);
    const setDate = bindActionCreators(setPlantingDate, dispatch);
    const setSoilHumidity = bindActionCreators(setHumidity, dispatch);
    const setSoilDensity = bindActionCreators(setDensity, dispatch);
    const setSoilEnvironment = bindActionCreators(setEnvironment, dispatch);

    const [searchInput, setSearchInput] = useState('');
    const setSelectedFeatureDispatch = bindActionCreators(setSelectedFeature, dispatch);
    const [showRecommendations, setShowRecommendations] = useState(false);
    const [recoSelected, setRecoSelected] = useState([]);
    const { recos, filterRecos, setFilterRecos, sortBy, setSortBy, setPage, count, page } = useMyRecommendations({
        checkOldRecos: false,
        startFilters: {
            field_id: selected,
            type: 'corn',
        },
    });
    const [showDialog, setShowDialog] = useState(false);

    const cropMonitoringsQuery = useCropMonitorings({
        page: 1,
        pageSize: 10000,
    });

    const cropMonitoringFromSameField = useMemo(
        () => cropMonitoringsQuery?.data?.results?.find((cm) => cm.field === selected && cm.active),
        [cropMonitoringsQuery, selected]
    );
    useEffect(() => {
        if (cropMonitoringFromSameField) {
            setShowDialog(true);
        }
    }, [cropMonitoringFromSameField]);

    const showBtns = recos.filter((r) => {
        return r.field_id === selected && r.type === 'corn';
    });

    const headers = [
        { name: _('Creación'), id: 'created' },
        { name: _('Tipo'), id: 'typeLabel' },
        { name: _('Fecha de siembra'), id: 'plantingDate' },
        { name: _('Nombre del lote'), id: 'fieldName' },
        { name: _('Ambiente'), id: 'environment' },
        { name: _('Producto'), id: 'product' },
    ];

    const [colsNum, setColsNum] = useState(3);

    useEffect(() => {
        const w = windowWidth;
        if (w >= 420) setColsNum(3);
        else setColsNum(2);
    }, [windowWidth]);

    useEffect(() => {
        setFilterRecos({ ...filterRecos, search: searchInput });
    }, [searchInput]);

    const handleInputFilter = (e) => {
        setSearchInput(e);
    };

    const handleClickCard = async () => {
        const data = {
            view: 'recommendations.RecommendationFieldSelect',
            selectedFeature: 'cropMonitoringCorn',
            crop: 'corn',
        };

        setSelectedFeatureDispatch(data);

        return history(
            seedRecommendationUrl({
                crop: 'corn',
                fieldId: selected,
                entrySource: 'crop-monitoring:creation',
                from: 'cropMonitoring',
            })
        );
    };

    const handleChargeManually = () => {
        setDate(undefined);
        setSoilHumidity(undefined);
        setSoilEnvironment(undefined);
        setSoilDensity(undefined);
        setSelectedReco(undefined);
        handleNext();
    };

    return (
        <Stack direction='column'>
            {!showBtns.length && (
                <Stack sx={isMobile && { position: 'relative', left: isMobileXl ? '-48px' : '-32px', width: '100vw' }}>
                    <Alert
                        severity='blue'
                        sx={{
                            my: 2,
                        }}
                    >
                        {_('Este lote ')}
                        <span className={styles.span}>{_('NO cuenta con Recomendaciones')}</span>
                        {_(', podés cargar los datos de ')}
                        <span className={styles.span}>{_('Manera Manual')}</span>
                        {_(' o ')}
                        <span className={styles.span}>{_('Generar una Recomendación.')}</span>
                    </Alert>
                </Stack>
            )}

            {((isMobile && !showRecommendations) || !isMobile) && (
                <Stack
                    direction={isMobile ? 'column' : 'row'}
                    spacing={2}
                    alignItems='stretch'
                    sx={{
                        py: 3,
                        ...(isMobile && {
                            py: 2,
                        }),
                    }}
                >
                    {Boolean(showBtns.length) && (
                        <Button
                            sx={{ width: !isMobile ? '33.33%' : '100%' }}
                            onClick={() => setShowRecommendations(true)}
                            variant='contained'
                            fullWidth
                        >
                            {_('Seleccionar una de Mis Recomendaciones')}
                        </Button>
                    )}
                    <Button
                        sx={{ width: !isMobile ? '33.33%' : '100%' }}
                        onClick={handleChargeManually}
                        variant='contained'
                        fullWidth
                    >
                        {_('Cargar datos Manualmente')}
                    </Button>
                    <Box sx={{ width: !isMobile ? '33%' : '100%' }}>
                        <Card
                            iconElement={<Icon size='md' type='cornRecoPlus' />}
                            name={_('Crear una nueva Recomendación')}
                            direction='row'
                            smaller
                            maxWidth
                            color={settings.app.theme === 'expertobayer' ? 'expertobayer' : 'orange'}
                            bolder
                            shadow={false}
                            className={styles.btn}
                            center
                            handleClick={handleClickCard}
                            border={false}
                        />
                    </Box>
                </Stack>
            )}
            {isMobile && showRecommendations && (
                <Stack direction='row' alignItems='flex-start' spacing={2} sx={{ py: 2 }}>
                    <Stack>
                        <Input
                            value={searchInput}
                            onChange={handleInputFilter}
                            color={settings.app.theme}
                            placeholder={_('Buscar por nombre, ubicación')}
                            onReset={() => setSearchInput('')}
                        />
                    </Stack>
                    <Stack>
                        {recos.length ? (
                            <Fragment>
                                {recos.map((row) => {
                                    return (
                                        <Stack
                                            sx={{ mb: 3, border: '0.5px solid #C4C4C4', borderRadius: 4, p: 2 }}
                                            key={row.id}
                                        >
                                            <MyRecommendationsMobileCard
                                                headers={headers}
                                                data={row}
                                                cols={colsNum}
                                                className={styles.card}
                                                isCropMonitoring
                                                setFilterRecos={setFilterRecos}
                                                setSearchInput={setSearchInput}
                                                setSelected={(e) => {
                                                    setRecoSelected(e);
                                                    setSelectedReco(e);
                                                    handleNext();
                                                }}
                                            />
                                        </Stack>
                                    );
                                })}
                            </Fragment>
                        ) : (
                            <Box sx={{ py: 6 }}>
                                <Drawing
                                    size='sm'
                                    type='library-not-found'
                                    title={_('No se encontraron resultados para tu búsqueda.')}
                                />
                            </Box>
                        )}
                    </Stack>
                </Stack>
            )}
            {showRecommendations && !isMobile && (
                <Stack direction='column' spacing={2}>
                    <Input
                        value={searchInput}
                        onChange={handleInputFilter}
                        color={settings.app.theme}
                        placeholder={_('Buscar por nombre, ubicación')}
                        onReset={() => setSearchInput('')}
                    />

                    <MyRecommendationsTable
                        selected={recoSelected}
                        rows={recos}
                        count={count}
                        page={page}
                        setSelected={setRecoSelected}
                        noDot
                        isCropMonitoring
                        handleNext={handleNext}
                        sortBy={sortBy}
                        setSortBy={setSortBy}
                        setPage={setPage}
                    />
                </Stack>
            )}
            <Dialog open={showDialog}>
                <DialogContent>
                    <Stack spacing={2}>
                        <Drawing type='warning-corn' />
                        <Typography variant='h6'>{_('Este lote ya posee un seguimiento de maíz activo.')}</Typography>
                        <Typography variant='body1'>
                            {_('Seleccione otro lote o finalice el seguimiento del lote actual.')}
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            handleBack();
                        }}
                        color={'secondary'}
                    >
                        {_('Seleccionar otro lote')}
                    </Button>
                    <Button
                        onClick={() => {
                            if (settings.app.theme === 'expertobayer') {
                                history(
                                    reverse('fields.FieldDetail', {
                                        params: { fieldId: cropMonitoringFromSameField?.field },
                                    })
                                );
                            } else {
                                history(
                                    reverse('cropMonitoring.CornCropMonitoringDetail', {
                                        params: { cropMonitoringId: cropMonitoringFromSameField?.id },
                                    })
                                );
                            }
                        }}
                        color='primary'
                        variant='contained'
                    >
                        {_('Ir a seguimiento activo')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default DataSourceStepper;
